import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2, Save } from 'lucide-react';
import { GlassCard } from '@/components/custom/GlassCard';
import { Button } from '@/components/ui/button';
import {
  fetchEscalationCandidates, fetchEscalationTarget, saveEscalationTarget, type EscalationCandidate,
} from '@/hooks/useAdminSettings';

// Admin-only screen to configure the escalation CHAIN (two levels since 2026-08-03) — level 1 is the
// colleague validators escalate to; level 2 (optional) is who the LEVEL-1 person escalates to (e.g.
// Jérémy → Anouk; a level-2 person may never log in — a passive user row is enough, the real
// reassignment happens in Uniticket). Picked from our own active internal users.
export function EscalationSettings() {
  const { t } = useTranslation('common');
  const [candidates, setCandidates] = useState<EscalationCandidate[]>([]);
  const [selected, setSelected] = useState('');
  const [selectedL2, setSelectedL2] = useState('');
  const [currentLabel, setCurrentLabel] = useState<string | null>(null);
  const [currentL2Label, setCurrentL2Label] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cand, target] = await Promise.all([fetchEscalationCandidates(), fetchEscalationTarget()]);
      setCandidates(cand.data);
      setSelected(target.data.level1?.userId ?? '');
      setCurrentLabel(target.data.level1?.label ?? null);
      setSelectedL2(target.data.level2?.userId ?? '');
      setCurrentL2Label(target.data.level2?.label ?? null);
    } catch (err: any) {
      toast.error(err?.message || t('escalation.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    if (!selected) {
      toast.error(t('escalation.requiredFields'));
      return;
    }
    setSaving(true);
    try {
      const res = await saveEscalationTarget(selected, selectedL2 || null);
      setCurrentLabel(res.data.level1?.label ?? null);
      setCurrentL2Label(res.data.level2?.label ?? null);
      toast.success(t('escalation.saved'));
    } catch (err: any) {
      toast.error(err?.message || t('escalation.saveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <ArrowUpRight className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{t('escalation.title')}</h2>
          <p className="text-sm text-slate-500">{t('escalation.subtitle')}</p>
        </div>
      </div>

      <GlassCard className="p-6 max-w-xl space-y-4">
        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">…</p>
        ) : candidates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-700">
            {t('escalation.noCandidates')}
          </div>
        ) : (
          <>
            {!currentLabel && (
              <div className="rounded-lg border border-dashed border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-700">
                {t('escalation.currentNone')}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600">{t('escalation.fieldUser')}</label>
              <select
                value={selected}
                onChange={(e) => {
                  setSelected(e.target.value);
                  // Level 2 must stay a DIFFERENT person — drop it if the new level 1 collides.
                  if (e.target.value && e.target.value === selectedL2) setSelectedL2('');
                }}
                className="mt-1 w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
              >
                <option value="">{t('escalation.selectPlaceholder')}</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.displayName} ({c.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{t('escalation.fieldUserL2')}</label>
              <select
                value={selectedL2}
                onChange={(e) => setSelectedL2(e.target.value)}
                className="mt-1 w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"
              >
                <option value="">{t('escalation.noneOption')}</option>
                {candidates.filter((c) => c.id !== selected).map((c) => (
                  <option key={c.id} value={c.id}>{c.displayName} ({c.email})</option>
                ))}
              </select>
              {currentL2Label && (
                <p className="mt-1 text-[11px] text-slate-400">{t('escalation.currentL2', { label: currentL2Label })}</p>
              )}
            </div>
            <p className="text-[11px] text-slate-400">{t('escalation.hint')}</p>
            <p className="text-[11px] text-slate-400">{t('escalation.chainHint')}</p>
            <div className="pt-1">
              <Button onClick={handleSave} disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                {t('escalation.save')}
              </Button>
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
}
